import { twJoin } from 'tailwind-merge';

import {
  Button,
  Icon,
  Link,
  RyeconClock,
  RyeconColorSnowflake,
  RyeconDatabase,
  RyeconDatabaseConnection,
  RyeconDatabaseSchema,
  Sizes,
  Tooltip,
} from '@dbt-labs/sourdough';

import {
  ConnectionNode,
  DatabaseNode,
  SchemaNode,
  WarehouseEntityType,
} from '@/__generated__/graphql';
import { Page } from '@/components/Page';
import { LinkCell } from '@/components/Table/LinkCell';
import { isWarehouseAssetId } from '@/context/AccountNavigation/utils';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useFavoriteAssets } from '@/hooks/admin/favoriteAssets';
import { useWarehouseEntities } from '@/hooks/discovery/catalog/entities';
import { useValidatedParams } from '@/hooks/params';
import { FavoriteAssetToggle } from '@/pages/account/components/FavoriteToggle';
import { ResourceFilterTable } from '@/pages/account/project/resource/components/ResourceFilterTable';
import {
  HeaderIconItem,
  TextIconPair,
} from '@/pages/account/project/resource/details/components/Header';
import { formatDateCommon, toRelativeDate } from '@/util/date';
import { AssetBreadcrumbs } from '../AssetBreadcrumbs';

const assetIconMapping = {
  [WarehouseEntityType.Connection]: RyeconDatabaseConnection,
  [WarehouseEntityType.Database]: RyeconDatabase,
  [WarehouseEntityType.Schema]: RyeconDatabaseSchema,
};

const assetTypeMapping = {
  [WarehouseEntityType.Connection]: WarehouseEntityType.Database,
  [WarehouseEntityType.Database]: WarehouseEntityType.Schema,
};

export const AssetFilterPage = ({
  asset,
}: {
  asset: DatabaseNode | SchemaNode | ConnectionNode | undefined;
}) => {
  const { accountId, assetId } = useValidatedParams();
  const pageSize = 10;
  const catalogAssetLink = useLinkToExplorer('catalogAsset');
  const { data: favoriteAssets } = useFavoriteAssets(accountId);
  const isFavorite = favoriteAssets?.some(
    (favoriteAsset) => favoriteAsset.asset_id === asset?.urn,
  );

  const {
    data: entities,
    fetching,
    pageInfo,
    fetchMore,
  } = useWarehouseEntities({
    accountId,
    first: pageSize,
    filter: {
      urn: '',
      type: isWarehouseAssetId(assetId)
        ? WarehouseEntityType.Connection
        : assetTypeMapping[asset?.type as keyof typeof assetTypeMapping],
      parent_urn: isWarehouseAssetId(assetId) ? null : (assetId ?? ''),
    },
  });

  const headerIcons: TextIconPair[] = [];

  if (asset?.type && asset.type !== WarehouseEntityType.Connection) {
    headerIcons.push({
      ryecon: assetIconMapping[asset.type as keyof typeof assetIconMapping],
      text: `${asset.type}`,
    });

    headerIcons.push({
      ryecon: RyeconClock,
      text: `Created ${toRelativeDate(
        asset.created ? new Date(asset.created) : new Date(),
      )}`,
      tooltip: formatDateCommon(asset.created ? new Date(asset.created) : new Date())
        .humanized,
    });
  }

  return (
    <Page title="Asset Filter Page" pageType="assetDetails">
      <AssetBreadcrumbs />
      <div className="mb-2 flex justify-between">
        <div className="flex items-center gap-2">
          {asset?.type &&
            [WarehouseEntityType.Database].includes(
              asset?.type as WarehouseEntityType,
            ) && <Icon ryecon={RyeconColorSnowflake} size="lg" />}
          <span className="text-xl font-bold">{asset?.name ?? ''}</span>
        </div>
        <div className="flex items-center gap-2">
          <FavoriteAssetToggle
            iconSize="sm"
            assetId={asset?.urn ?? ''}
            assetType={asset?.type}
            platform={asset?.platform ?? ''}
            name={asset?.name ?? ''}
            isFavorite={isFavorite}
          />
          {asset && 'externalUrl' in asset && (
            <Link isInternal={false} to={asset?.externalUrl ?? ''} target="_blank">
              <Button size={'sm' as Sizes} text="Open in Snowflake" />
            </Link>
          )}
        </div>
      </div>
      <div
        className={twJoin(
          'mb-4 flex gap-4 duration-300 motion-reduce:duration-0',
          headerIcons.length ? 'opacity-100' : 'opacity-0',
        )}
      >
        {headerIcons.map(({ key, ...props }: TextIconPair) => {
          if (props.href) {
            return (
              <Link key={props.text ?? key} isInternal={false} to={props.href}>
                <HeaderIconItem {...props} />
              </Link>
            );
          }
          return <HeaderIconItem key={props.text ?? key} {...props} />;
        })}
      </div>
      <ResourceFilterTable
        pluralizedResultName="models"
        data={entities}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            minSize: 60,
            maxSize: 240,
            cell: ({ ...props }) => (
              <LinkCell
                href={catalogAssetLink({
                  accountId: accountId ?? 0,
                  assetId: props.row.original.urn,
                })}
                {...props}
              />
            ),
          },
          {
            header: 'Created',
            accessorKey: 'created',
            cell: ({ getValue }) => {
              return (
                <Tooltip
                  content={
                    formatDateCommon(getValue() ? new Date(getValue()) : new Date())
                      .humanized
                  }
                >
                  {toRelativeDate(getValue() ? new Date(getValue()) : new Date())}
                </Tooltip>
              );
            },
          },
        ]}
        pageInfo={{
          totalCount: pageInfo?.totalCount ?? 0,
          endCursor: null,
          hasNextPage: !!pageInfo?.hasNextPage,
        }}
        isLoading={fetching && entities?.length === 0}
        isLoadingMore={fetching}
        pageSize={10}
        fetchMore={fetchMore}
      />
    </Page>
  );
};
